// box z cenami, jedostkami itp

import React, { type FC, type ReactNode, useEffect, useMemo, useState } from 'react';
import classnames from 'classnames';
import { Printer, Truck } from 'react-bootstrap-icons';
import { Trans } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { useSelector } from 'store';
import { type IPrice, type IProduct } from 'api/types';
import {
  useGetProductAddToCartHint,
  useGetCartDeliveryInfo,
  usePostProductQuantityDecrement,
  usePostProductQuantityIncrement,
  usePostProductQuantity
} from 'api';
import { useNotifications } from 'hooks';
import { Counter, Radio, Availability, Link } from 'components/controls';
import { AddToCartButton, AddToShoppingListButton, AskProductModal } from 'components/containers';

import { ReactComponent as MessageQuestionIcon } from 'assets/messages-question.svg';
import styles from 'theme/pages/Product/components/Prices/Prices.module.scss';

// typ danych wejściowych
interface IProps {
  product: IProduct;
  disableAddToCart?: boolean;
  compactProductPricesLayout?: boolean;
  wisePricesMainContentClassName?: string;
  wisePricesPriceSummaryClassName?: string;
  renderProductPriceOptions?: (args: {
    prices: IPrice[];
    unitId: number;
    onSelectUnit: (unitId: number) => void;
  }) => ReactNode;
}

const Prices: FC<IProps> = ({
  product,
  disableAddToCart = false,
  compactProductPricesLayout = false,
  wisePricesMainContentClassName,
  wisePricesPriceSummaryClassName,
  renderProductPriceOptions
}) => {
  const location = useLocation();
  const { showWarningMessage } = useNotifications();
  const { profile } = useSelector((state) => state.auth);

  // id domyślnego koszyka
  const { currentCartId } = useSelector((state) => state.cart);

  // ilość produktów
  const [quantity, setQuantity] = useState(0);

  // aktualnie zmieniona ilość (sprzed walidacji)
  const [manuallyChangedQuantity, setManuallyChangedQuantity] = useState<number>();

  // aktualne ID jednostki
  const [unitId, setUnitId] = useState<number>(
    product?.product_price_full_info?.prices?.[0]?.unit_id || 0
  );

  // aktualna jednostka
  const unit = useMemo(
    () => product?.product_price_full_info?.prices?.find((unit) => unit.unit_id === unitId),
    [unitId, product]
  );

  const isProductInfoBlocked =
    product.product_basic_full_info?.show_information_product_un_available_and_block_add_to_cart;

  // zmiana ilości po zmianie jednostki
  useEffect(() => {
    unit && setQuantity(unit.minimal_quantity || 1);
  }, [unit]);

  // pobranie informacji o zmniejsonej ilości
  const { mutate: decrementQuantity, isLoading: isDecrementQuantityLoading } =
    usePostProductQuantityDecrement(product.id, {
      onSuccess: (data) => {
        setQuantity(data.data.value);
      }
    });

  // pobranie informacji o zwiększonej ilości
  const { mutate: incrementQuantity, isLoading: isIncrementQuantityLoading } =
    usePostProductQuantityIncrement(product.id, {
      onSuccess: (data) => {
        setQuantity(data.data.value);
      }
    });

  // pobranie informacji o zwiększonej ilości
  const { mutate: updateQuantity, mutateAsync: updateQuantityAsync } = usePostProductQuantity(
    product.id,
    {
      onSuccess: (data) => {
        setQuantity(data.data.value);
      }
    }
  );

  // pobranie informacji z podpowiedzią do koszyka (tylko gdy dodawanie do koszyka włączone)
  const { data: addToCartHintData } = useGetProductAddToCartHint(
    product.id,
    {
      unit_id: unitId,
      quantity
    },
    { keepPreviousData: true, enabled: !disableAddToCart }
  );

  // pobranie podpowiedzi o dostawie
  const { data: cartDeliveryInfoData } = useGetCartDeliveryInfo(currentCartId || 0, {
    enabled:
      !!currentCartId && profile?.role !== 'ROLE_OPEN_PROFILE' && !compactProductPricesLayout
  });

  const isOpenProfile = profile?.role === 'ROLE_OPEN_PROFILE';

  // stan modala zapytania o produkt
  const [isAskProductModalOpen, setIsAskProductModalOpen] = useState(false);

  return (
    <div
      className={classnames(styles.componentWrapper, 'StylePath-Pages-Product-components-Prices')}>
      <div
        className={classnames(styles.mainContent, wisePricesMainContentClassName)}>
        {!isOpenProfile && !isProductInfoBlocked && (
          <div className={classnames(styles.price, wisePricesPriceSummaryClassName)}>
            <div className={styles.text}>
              <Trans>Twoja cena:</Trans>
            </div>

            <div className={styles.groupPrice}>
              <div className={styles.net}>
                {unit?.old_price_net_formatted && (
                  <div className={styles.old}>
                    {unit.old_price_net_formatted} {unit?.currency}
                  </div>
                )}
                <strong>
                  {unit?.price_net_formatted} {unit?.currency}
                </strong>{' '}
                <Trans>netto</Trans>
              </div>
              <div className={styles.gross}>
                {unit?.price_gross_formatted} {unit?.currency} <Trans>brutto</Trans>
              </div>
            </div>
          </div>
        )}

        {!isOpenProfile && !isProductInfoBlocked && (
          <>
            {compactProductPricesLayout && renderProductPriceOptions ? (
              renderProductPriceOptions({
                prices: product.product_price_full_info?.prices ?? [],
                unitId,
                onSelectUnit: setUnitId
              })
            ) : (
              <div className={styles.tableWrapper}>
                <table>
                  <thead>
                  <tr>
                    <th className={styles.left}>
                      <Trans>Typ opakowania</Trans>
                    </th>
                    <th>
                      <Trans>Ilość</Trans>{' '}
                      <span>
                          <Trans>szt. w opakowaniu</Trans>
                        </span>
                    </th>
                    <th>
                      <Trans>Cena</Trans>{' '}
                      <span>
                          (<Trans>netto</Trans>)
                        </span>
                    </th>
                    <th>
                      <Trans>Cena za szt.</Trans>{' '}
                      <span>
                          (<Trans>netto</Trans>)
                        </span>
                    </th>
                  </tr>
                  </thead>
                  <tbody>
                  {product?.product_price_full_info?.prices?.map((u) => (
                    <tr key={u.id} className={classnames({ current: u.unit_id === unitId })}>
                      <td className={styles.left}>
                          <span className={styles.nameWrapper}>
                            <Radio
                              checked={u.unit_id === unitId}
                              onClick={() => setUnitId(u.unit_id)}
                            />{' '}
                            {u.unit_name}
                          </span>
                      </td>
                      <td>{u.converter}</td>
                      <td>
                        {u.price_net_formatted} {u?.currency}
                      </td>
                      <td>
                        {u.unit_price_net_formatted} {u?.currency}
                      </td>
                    </tr>
                  ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {!!addToCartHintData && addToCartHintData.hint && !disableAddToCart && (
          <div
            className={styles.promotion}
            data-wise-prices-promotion={compactProductPricesLayout ? '' : undefined}
            style={{ borderColor: addToCartHintData.color, color: addToCartHintData.color }}>
            {addToCartHintData.hint}
          </div>
        )}

        {!disableAddToCart && (
        <div className={styles.actionsWrapper}>
          {isOpenProfile ? (
            <Link
              to={`/login?return_url=${encodeURIComponent(location.pathname + location.search)}`}
              className={styles.loginButton}>
              <Trans>zaloguj</Trans>
            </Link>
          ) : !isProductInfoBlocked ? (
            <Counter
              className={styles.quantityCounter}
              onChange={(value) => updateQuantity({ value, unit_id: unitId })}
              onDecrease={() => decrementQuantity({ value: quantity, unit_id: unitId })}
              onIncrease={() => incrementQuantity({ value: quantity, unit_id: unitId })}
              value={quantity}
              onChangeValue={(isChanged, newValue) => setManuallyChangedQuantity(newValue)}
            />
          ) : null}
          {!isProductInfoBlocked && (
            <AddToCartButton
              isQuantityChanges={!!manuallyChangedQuantity && manuallyChangedQuantity !== quantity}
              updateQuantity={async () => {
                const newQuantity = manuallyChangedQuantity
                  ? await updateQuantityAsync({
                    value: manuallyChangedQuantity,
                    unit_id: unitId
                  })
                  : undefined;

                return newQuantity?.data.value === manuallyChangedQuantity
                  ? manuallyChangedQuantity
                  : undefined;
              }}
              quantity={quantity}
              unitId={unitId}
              productId={product.id}
              large
              disabled={isOpenProfile || isDecrementQuantityLoading || isIncrementQuantityLoading}
            />
          )}
        </div>
        )}
      </div>

      {!compactProductPricesLayout && (
        <>
          <div className={styles.dashedBlock}>
            <div className={styles.item}>
              {product.product_stock_formatted_full_info && (
                <Availability stock={product.product_stock_formatted_full_info} />
              )}
            </div>

            {cartDeliveryInfoData?.info && (
              <div className={styles.item}>
                <Truck />
                {cartDeliveryInfoData.info}
              </div>
            )}
          </div>

          <div className={styles.dashedBlock}>
            <div
              className={classnames(styles.item, styles.button)}
              onClick={() => showWarningMessage('Funkcja niezaimplementowana')}>
              <Printer />
              <Trans>Drukuj</Trans>
            </div>

        {!isOpenProfile && !disableAddToCart && (
          <div className={styles.item}>
            <AddToShoppingListButton
              product_id={product.id}
              unit_id={unit?.unit_id}
              image={product?.product_image_formatted_full_info?.images?.[0]?.thumb}
              title={product?.product_basic_full_info?.title}
              index={product?.product_basic_full_info?.index}
              currency={unit?.currency}
              quantity={quantity}
              unit={unit}
            />
          </div>
        )}
        <div
          className={classnames(styles.item, styles.button)}
          onClick={() => setIsAskProductModalOpen(true)}>
          <MessageQuestionIcon />
          <Trans>Zapytaj o produkt</Trans>
        </div>
      </div>

          <AskProductModal
            product={product}
            open={isAskProductModalOpen}
            onClose={() => setIsAskProductModalOpen(false)}
          />
        </>
      )}
    </div>
  );
};

export default Prices;
